import { showDialog } from '@/utils/dialogs';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import {
  View,
  FlatList,
  ActivityIndicator,
  Platform,
} from 'react-native';

// React Native Reusables Components
import { Text } from '@/components/ui/text';
import { WebAppShell } from '@/design-system/components';

// Credit Components
import { ZeroCreditBlocker, CreditPurchaseModal, LowCreditWarningBanner } from '@/components/credits';
import { useCreditCheck } from '../../context/CreditContext';

// Auth context for tier checks
import { useAuth } from '../../context/AuthContext';

// Services & Types
import { authAPI, chatAPI, transcriptionAPI, userDocumentsAPI, type UserDocumentItem } from '../../services/api';
import { useIdempotencyIntent } from '../../services/idempotencyIntent';
import { ChatMessage, ChatSession, UnifiedAIResponse } from '../../types';
import { ROUTES } from '../../navigation/routes';
import { useTranslation } from '../../hooks/useTranslation';
import { useToast } from '../../context/ToastContext';
import { useWaitingTips } from '../../hooks/useWaitingTips';
import { useTaskCompletion } from '../../hooks/useTaskCompletion';
import { markActivated, recordAnalysisCompleted, trackAuthEvent } from '../../services/analytics';
import { useTips } from '../../context/TipsContext';
import { getAnalysisTierAccess } from '../../utils/planContext';
import { canTrackIcfProgress, chatLockMode, hasEntitlement } from '../../utils/billingAccess';
import { AnalysisModal } from '../../components/analysis/AnalysisModal';
import { useTrialContext } from '../../context/TrialContext';
import { useAccessPolicy } from '../../hooks/useAccessPolicy';
import { useResponsive } from '../../hooks/useResponsive';
import TrialBanner from '../../components/trial/TrialBanner';
import TrialChatLimitModal from '../../components/trial/TrialChatLimitModal';
import TrialResultsLock from '../../components/trial/TrialResultsLock';
import TrialExpiredModal from '../../components/trial/TrialExpiredModal';
import { ShareDocumentModal } from '../../components/sharing/ShareDocumentModal';
import SampleReportViewer from '../../components/onboarding/SampleReportViewer';
import { createConversationGuard } from './conversationGuard';
import { hasLinkedTranscript, isSessionAnalyzed } from './sessionMeta';
import {
  COLORS,
  PENDING_TASKS_KEY_PREFIX,
  WAITING_BUBBLE_ID,
} from './chatConstants';
import { webChatStyles } from './chatStyles';
import { ChatThread } from './components/ChatThread';
import { ChatMessageRow } from './components/ChatMessageRow';
import { SessionsSheet } from './components/SessionsSheet';
import { RenameSessionDialog, UploadPromptDialog } from './components/ChatDialogs';
import { ChatHeader } from './components/ChatHeader';
import { ChatUsageStrip } from './components/ChatUsageStrip';
import { ChatContextPanel } from './components/ChatContextPanel';
import { ChatComposer } from './components/ChatComposer';
import { ActivationCelebration } from './components/ActivationCelebration';
import { buildChatUsageCards, creditChipLabel, trialUsageFrom } from './chatUsage';
import { buildChatRecentItems, chatSuggestedPrompts, composerPlaceholder } from './chatViewModel';
import { mergeOptimisticMessages, sortMessagesChronologically, withPendingBubbles } from './messageMerge';


// Voice Transcription Components
import {
  TranscriptionModal,
  TranscriptionProgress,
  TranscriptViewer,
  AnalysisProgress,
} from '../../components/voice';

// WebSocket for real-time task progress
import { useWebSocketConnection } from '../../hooks/useTaskProgress';
import { useReferenceFiles } from './useReferenceFiles';
import { useChatStreaming } from './useChatStreaming';
import { useChatQuota } from './useChatQuota';
import { usePendingTasks } from './usePendingTasks';
import { useChatOperationRefs } from './useChatOperationRefs';
import { useChatToolCalls } from './useChatToolCalls';
import { useDocumentAnalysis } from './useDocumentAnalysis';
import { useVoiceTranscription } from './useVoiceTranscription';
import { useChatSend } from './useChatSend';
import { useSessionExports } from './useSessionExports';
import { useSessionActions } from './useSessionActions';

/**
 * Enhanced Chat Screen - Professional Mobile-First Design
 *
 * Features:
 * - Uses React Native Reusables for consistent professional UI
 * - Complete i18n support with no hardcoded text
 * - Fixed session management with proper persistence
 * - Responsive layout adapting to screen size
 * - Proper scroll fix pattern applied
 * - Modal redesigns matching design system
 *
 * Bug Fixes:
 * - Fixed session switching race conditions
 * - Improved session persistence
 * - Removed complex ref-based workarounds
 * - Simplified state management
 */

export default function ChatScreenEnhanced() {
  const { t, language } = useTranslation();
  const { showToast } = useToast();
  const router = useRouter();
  const { isPhone, isCompact } = useResponsive();
  // Auth context for tier checks
  const { billingStatus, user } = useAuth();
  const { isB2BUser, isSuperAdmin } = useAccessPolicy();
  // One rule with Library (CHAT-22): the server's icf_progress entitlement.
  const canIncludeInIcfProgress = canTrackIcfProgress(billingStatus, {
    isSuperAdmin,
    isSchoolMember: isB2BUser,
  });
  // Past reports stay readable for them too, including an expired trial session.
  const canViewOwnReports = hasEntitlement(billingStatus, 'view_own_reports', false);
  const pendingTasksStorageKey = user?.id
    ? `${PENDING_TASKS_KEY_PREFIX}:${user.id}`
    : null;

  // WebSocket-based task completion (replaces HTTP polling)
  const { waitForTask, waitForTaskResult } = useTaskCompletion();

  // Contextual tips system
  const { showTip } = useTips();
  // Refs shared by the send, tool-call, analysis and transcription flows.
  const ops = useChatOperationRefs();
  const {
    sessionHasAnalysisRef,
    handleToolCallsRef,
    pickAudioForTranscriptionRef,
    optimisticMessagesRef,
    taskWaiterRef,
    shouldAutoScrollRef,
    pendingScrollToBottomRef,
    activeOperationSessionRef,
    isAnalyzingInProgressRef,
    isSendingInProgressRef,
    suppressBubbleRef,
    sendingMessageRef,
    analyzingFileRef,
  } = ops;

  // Credit check for document analysis
  const { showBlocker, checkCredits, hideBlocker, refreshStatus, creditStatus } = useCreditCheck();
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const {
    quotaStatus,
    quotaLoadFailed,
    audioUploadLimits,
    transcriptionUsage,
    loadQuotaStatus,
    loadAudioUploadLimits,
    loadTranscriptionUsage,
  } = useChatQuota();

  // Purchased credits allow users to exceed the subscription's monthly transcript limit.
  // Only treat the transcript limit as truly exceeded when the user has NO purchased credits left.
  const hasPurchasedCredits = (creditStatus?.purchased_credits ?? 0) > 0;

  const params = useLocalSearchParams<{
    sessionId?: string | string[];
    startNew?: string | string[];
    showSessionsList?: string | string[];
    suggestedQuestion?: string | string[];
  }>();

  const getFirst = (value?: string | string[]) =>
    Array.isArray(value) ? value[0] : value;

  const sessionIdParam = getFirst(params.sessionId);
  const startNewParam = getFirst(params.startNew);
  const showSessionsListParam = getFirst(params.showSessionsList);
  const suggestedQuestionParam = getFirst(params.suggestedQuestion);

  // =================== STATE ===================

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(true);
  const [, setLoadingMessages] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [analyzingFile, setAnalyzingFile] = useState(false);
  // Modal states
  const [showSessionsList, setShowSessionsList] = useState(false);
  const [showActivationCelebration, setShowActivationCelebration] = useState(false);
  const [showSampleReport, setShowSampleReport] = useState(false);
  const [intakeGoal, setIntakeGoal] = useState<string | null>(null);

  const handleAnalysisMilestone = useCallback(async (via: string) => {
    const completionCount = await recordAnalysisCompleted();
    const firstActivation = await markActivated({ via });
    if (firstActivation) {
      trackAuthEvent('activation_celebration_shown', { via });
      setShowActivationCelebration(true);
      return;
    }
    if (completionCount === 2) {
      showTip('tip_icf_progress');
      if (intakeGoal && ['ACC', 'PCC', 'MCC'].includes(intakeGoal.toUpperCase())) {
        showTip('tip_practice_exam');
      }
    }
  }, [intakeGoal, showTip]);

  useEffect(() => {
    let cancelled = false;
    authAPI.getIntakeSummary()
      .then((summary) => {
        if (!cancelled) setIntakeGoal(summary?.primary_goal || null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Inline composer attachment menu (web). Toggles a small popover above the
  // text input with two choices: analyze-transcript / add-reference.
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  // Drag-and-drop visual state on the web composer wrapper.
  const [dragOver, setDragOver] = useState(false);
  const composerWrapRef = React.useRef<any>(null);

  // Trial context
  const { trialStatus, isTrialSession, refreshTrialStatus } = useTrialContext();
  const [showTrialLimitModal, setShowTrialLimitModal] = useState(false);
  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  const trialExpiredModalShown = React.useRef(false);
  const isTrialExhausted = trialStatus?.active
    && currentSessionId !== null
    && isTrialSession(currentSessionId)
    && trialStatus.messages_remaining <= 0;

  // Show expired modal once when user visits an expired trial session
  React.useEffect(() => {
    if (
      trialStatus?.expired &&
      !canViewOwnReports &&
      currentSessionId !== null &&
      isTrialSession(currentSessionId) &&
      !trialExpiredModalShown.current
    ) {
      trialExpiredModalShown.current = true;
      setShowTrialExpiredModal(true);
    }
  }, [trialStatus?.expired, canViewOwnReports, currentSessionId]);

  // "Analysis completed" comes from the session's server metadata or from an
  // analysis task this screen watched finish (CHAT-7), never from reply text.
  const [completedAnalysisSessionIds, setCompletedAnalysisSessionIds] = useState<ReadonlySet<number>>(() => new Set());
  const markSessionAnalyzed = useCallback((sessionId: number | null | undefined) => {
    if (sessionId == null) return;
    setCompletedAnalysisSessionIds((prev) => (prev.has(sessionId) ? prev : new Set(prev).add(sessionId)));
  }, []);
  const currentSession = currentSessionId != null
    ? sessions.find((session) => session.id === currentSessionId)
    : undefined;
  const sessionAnalyzed = isSessionAnalyzed(currentSessionId, currentSession, completedAnalysisSessionIds);
  // D3: credit-only users (lapsed subscription, purchased credits) keep
  // analyses and past reports here, but plain chat needs a subscription. In a
  // session with a transcript they can still re-assess it for one credit
  // (`chat_reassess`); the server refuses anything else with 402. Only the
  // server payload can lock chat; an older backend without it never locks.
  const chatMode = chatLockMode(billingStatus, { sessionHasTranscript: hasLinkedTranscript(currentSession) });
  const chatLocked = chatMode === 'locked';
  const reassessOnly = chatMode === 'reassess_only';
  sessionHasAnalysisRef.current = sessionAnalyzed;

  useEffect(() => {
    const openedSessions = sessions.filter((session) => session.session_type !== 'welcome').length;
    if (openedSessions >= 3) {
      showTip('tip_documents_rag');
    }
  }, [sessions, showTip]);

  useEffect(() => {
    if (isB2BUser && sessionAnalyzed) {
      showTip('tip_meeting_feedback');
    }
  }, [isB2BUser, sessionAnalyzed, showTip]);

  // Tier access for analysis options
  const tierAccess = getAnalysisTierAccess(billingStatus?.plan_tier);

  // Voice Transcription states
  const [showTranscriptViewer, setShowTranscriptViewer] = useState(false);
  const [viewingTranscript, setViewingTranscript] = useState<any>(null);
  const [recentDocuments, setRecentDocuments] = useState<UserDocumentItem[]>([]);

  // WebSocket task tracking
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const { useFallback: wsUnavailable } = useWebSocketConnection();

  // Quota tracking
  const activeTrialUsage = trialUsageFrom(trialStatus);
  const paidChatLimitExceeded = Boolean(!trialStatus?.active && quotaStatus?.chatLimitExceeded);
  const transcriptLimitReached = Boolean(
    activeTrialUsage
      ? activeTrialUsage.analysisRemaining <= 0
      : quotaStatus &&
        quotaStatus.usage.monthly_transcripts_used >= quotaStatus.usage.monthly_transcripts_limit
  );
  const transcriptBlocked =
    transcriptLimitReached &&
    !hasPurchasedCredits &&
    creditStatus?.credit_exempt !== true &&
    creditStatus?.pool_available !== true;
  const getSessionSourceFilename = useCallback((session?: ChatSession | null) => {
    if (!session) return '';
    if (session.source_document_filename) return session.source_document_filename;
    if (session.source_document_id) {
      return recentDocuments.find((document) => document.id === session.source_document_id)?.original_filename || '';
    }
    return '';
  }, [recentDocuments]);
  const getSessionDisplayTitle = useCallback((session?: ChatSession | null) => {
    if (!session) return '';
    return getSessionSourceFilename(session) || session.title || `${t('chat.session')} ${session.id}`;
  }, [getSessionSourceFilename, t]);
  const isWaitingForResponse = sendingMessage || analyzingFile;
  const waitingTipKey = useWaitingTips(isWaitingForResponse);
  const flatListRef = useRef<FlatList>(null);
  // Which conversation is on screen; replaces the old isStartingNewRef /
  // loadingSessionIdRef / latestSessionIdRef trio (CHAT-3/4/5/15).
  const conversationGuardRef = useRef(createConversationGuard());
  const guard = conversationGuardRef.current;
  const messagesRef = useRef<ChatMessage[]>([]);
  const latestSessionsCountRef = useRef(0);
  // ASYNC-6: one Idempotency-Key per user intent. A double submit of the same
  // input reuses it (the backend replays the existing job); "Try again" after
  // a failure mints a new one, since the old key would return the failed job.
  // A typed message the server may turn into a billed re-assessment. Re-sending
  // the restored draft after a failed request reuses the key; once the server
  // has answered, the next send is a new intent.
  const chatSendIntent = useIdempotencyIntent();

  const showTranscriptLimitWarning = useCallback(() => {
    if (!quotaStatus) return;
    trackAuthEvent('chat_quota_exceeded', {
      limit_type: 'monthly_transcripts',
      used: quotaStatus.usage.monthly_transcripts_used,
      limit: quotaStatus.usage.monthly_transcripts_limit,
    });
    const message = `${t('chat.transcriptQuotaExceeded')}\n\n${t('chat.monthlyTranscriptLimitMessage').replace('{{limit}}', String(quotaStatus.usage.monthly_transcripts_limit))}`;
    showDialog(message);
  }, [quotaStatus, t]);

  // Bug fix: debounced scroll to prevent erratic up-down-up-down bouncing
  // caused by 7+ independent scroll-to-bottom triggers firing within ~500ms
  const scrollDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scrollToBottomDebounced = useCallback((animated = true) => {
    if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current);
    scrollDebounceRef.current = setTimeout(() => {
      if (pendingScrollToBottomRef.current) {
        flatListRef.current?.scrollToEnd({ animated });
        pendingScrollToBottomRef.current = false;
      }
      scrollDebounceRef.current = null;
    }, 150);
  }, [pendingScrollToBottomRef]);

  const queueScrollToBottom = useCallback((animated = true) => {
    pendingScrollToBottomRef.current = true;
    scrollToBottomDebounced(animated);
  }, [pendingScrollToBottomRef, scrollToBottomDebounced]);

  // Cleanup scroll debounce on unmount
  useEffect(() => {
    return () => { if (scrollDebounceRef.current) clearTimeout(scrollDebounceRef.current); };
  }, []);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const {
    pendingTasks,
    addPendingTask,
    removePendingTask,
    getPendingTaskForSession,
  } = usePendingTasks(pendingTasksStorageKey);

  useEffect(() => { sendingMessageRef.current = sendingMessage; }, [sendingMessage, sendingMessageRef]);
  useEffect(() => { analyzingFileRef.current = analyzingFile; }, [analyzingFile, analyzingFileRef]);

  const addOptimisticMessage = useCallback((sessionId: number, message: ChatMessage) => {
    const existing = optimisticMessagesRef.current.get(sessionId) || [];
    optimisticMessagesRef.current.set(sessionId, [...existing, message]);
  }, [optimisticMessagesRef]);

  useEffect(() => {
    latestSessionsCountRef.current = sessions.length;
  }, [sessions.length]);

  /**
   * Put a session on screen because the user picked it (history, recent
   * document, deep link). The previous thread is cleared at once so
   * nothing from the old session shows under the new title while the new
   * one loads (CHAT-4); useReferenceFiles hides the old chips (CHAT-5).
   */
  const showSession = useCallback((sessionId: number) => {
    const changed = guard.startingNew || guard.viewSessionId !== sessionId;
    guard.select(sessionId);
    if (changed) {
      setMessages([]);
    }
    setCurrentSessionId(sessionId);
  }, [guard]);

  /**
   * Adopt a session a flow just created or was handed by the server for the
   * conversation already on screen ("New chat" + send / attach / analysis).
   * Clears "New chat" mode so the next message goes to the same session
   * (CHAT-3); keeps the optimistic bubbles on screen.
   */
  const adoptSession = useCallback((sessionId: number) => {
    guard.select(sessionId);
    setCurrentSessionId(sessionId);
  }, [guard]);

  /** Clear the view (deleted session). */
  const clearSession = useCallback(() => {
    guard.select(null);
    setCurrentSessionId(null);
    setMessages([]);
  }, [guard]);

  // =================== DATA LOADING ===================

  // Simple session loader - NO auto-selection logic here (moved to effect)
  // This avoids race conditions from stale closure values
  const loadSessions = useCallback(async (options?: { autoSelectFirst?: boolean }) => {
    try {
      const sessionList = await chatAPI.getSessions();
      const sortedSessions = [...sessionList].sort((a, b) => {
        const aTime = new Date(a.created_at).getTime();
        const bTime = new Date(b.created_at).getTime();
        return bTime - aTime;
      });
      setSessions(sortedSessions);

      // Auto-select ONLY if explicitly requested and conditions are met
      // This is called synchronously after we know the user's intent
      if (options?.autoSelectFirst && sortedSessions.length > 0 && !guard.startingNew) {
        showSession(sortedSessions[0].id);
      }

      return sortedSessions;
    } catch (error) {
      console.error('Failed to load sessions:', error);
      showDialog(t('chat.failedToLoadSessions'));
      return [];
    }
  }, [guard, showSession, t]);

  const loadRecentDocuments = useCallback(async () => {
    try {
      const response = await userDocumentsAPI.list(0, 4);
      setRecentDocuments(response.documents || []);
    } catch (error) {
      console.warn('Failed to load recent documents:', error);
      try {
        const transcriptResponse = await transcriptionAPI.list(0, 4);
        setRecentDocuments((transcriptResponse.transcripts || []).map((item) => ({
          id: item.id,
          document_type: 'user_transcript',
          original_filename: item.original_filename,
          file_size: 0,
          content_type: 'audio/*',
          created_at: item.created_at,
          audio_duration_seconds: item.audio_duration_seconds,
          transcript_language: item.transcript_language,
          transcription_status: item.transcription_status,
          grammar_correction_applied: item.grammar_correction_applied,
        })));
      } catch (fallbackError) {
        console.warn('Failed to load recent transcripts:', fallbackError);
      }
    }
  }, []);

  const loadMessages = useCallback(async (sessionId: number) => {
    // Only the session on screen may load into the view: a reload for a
    // session the user switched away from (or while "New chat" is empty)
    // would paint another conversation under this one's title (CHAT-4).
    if (!guard.isViewing(sessionId)) {
      return;
    }

    // Every load gets a token; only the newest load for a session that is
    // still on screen is applied, so a slow earlier load can't overwrite a
    // newer one (e.g. the post-send reload) and the in-flight flag is
    // tracked per load instead of one shared boolean (CHAT-15).
    const loadToken = guard.beginLoad(sessionId);
    setLoadingMessages(true);

    try {
      const messageList = await chatAPI.getMessages(sessionId);

      if (!guard.shouldApplyLoad(loadToken, sessionId)) {
        return;
      }

      // Merge server messages with optimistic + pending state
      const optimistic = optimisticMessagesRef.current.get(sessionId) || [];
      const { merged, optimisticToAdd, confirmedSome } = mergeOptimisticMessages(messageList, optimistic);
      let mergedList = merged;

      // Prune optimistic messages confirmed by server
      if (confirmedSome) {
        if (optimisticToAdd.length === 0) {
          optimisticMessagesRef.current.delete(sessionId);
        } else {
          optimisticMessagesRef.current.set(sessionId, optimisticToAdd);
        }
      }

      // Only add thinking bubble if not suppressed (suppressed when operation just completed
      // but React state hasn't re-rendered yet, causing stale analyzingFile/sendingMessage values)
      if (!suppressBubbleRef.current) {
        mergedList = withPendingBubbles(mergedList, {
          sessionId,
          pendingTask: getPendingTaskForSession(sessionId),
          operationBusyOnSession:
            (sendingMessageRef.current || analyzingFileRef.current) &&
            activeOperationSessionRef.current === sessionId,
          waitingTipKey,
          t,
        });
      }

      // Sort by created_at, then by id for tiebreaking
      sortMessagesChronologically(mergedList);

      setMessages(mergedList);
      queueScrollToBottom(true);
    } catch (error) {
      console.error('Failed to load messages:', error);
      if (!guard.shouldApplyLoad(loadToken, sessionId)) return;
      showDialog(t('chat.failedToLoadMessages'));
    } finally {
      guard.finishLoad(loadToken);
      setLoadingMessages(guard.isLoading());
    }
  }, [
    guard,
    t,
    waitingTipKey,
    getPendingTaskForSession,
    queueScrollToBottom,
    // Stable refs from useChatOperationRefs:
    activeOperationSessionRef,
    analyzingFileRef,
    optimisticMessagesRef,
    sendingMessageRef,
    suppressBubbleRef,
  ]);

  useEffect(() => {
    if (!currentSessionId) return;
    // Skip during active operations — main flow handles everything
    if (isAnalyzingInProgressRef.current || isSendingInProgressRef.current) return;
    const pending = getPendingTaskForSession(currentSessionId);
    if (pending) {
      loadMessages(currentSessionId);
    }
  }, [pendingTasks, currentSessionId, getPendingTaskForSession, loadMessages, isAnalyzingInProgressRef, isSendingInProgressRef]);

  useEffect(() => {
    if (!currentSessionId) return;
    const pending = getPendingTaskForSession(currentSessionId);
    if (!pending) return;
    if (taskWaiterRef.current.has(pending.taskId)) return;

    taskWaiterRef.current.add(pending.taskId);
    const timeoutMs = pending.kind === 'analysis' ? 600_000 : 300_000;
    waitForTask(pending.taskId, {
      timeoutMs,
    })
      .then((result) => {
        const toolResult = (result as any)?.result as UnifiedAIResponse | undefined;
        if (toolResult?.status === 'WAITING_FOR_CLIENT_TOOL' && toolResult.pending_tool_calls?.length) {
          handleToolCallsRef.current(toolResult.pending_tool_calls, toolResult.detected_intent);
          return;
        }
        removePendingTask(pending.taskId);
        loadMessages(pending.sessionId);
      })
      .catch(() => {
        removePendingTask(pending.taskId);
        loadMessages(pending.sessionId);
      })
      .finally(() => {
        taskWaiterRef.current.delete(pending.taskId);
      });
  }, [currentSessionId, getPendingTaskForSession, loadMessages, removePendingTask, waitForTask, t, handleToolCallsRef, taskWaiterRef]);

  // =================== EFFECTS ===================

  // Track if initial load has happened to avoid duplicate loads
  const initialLoadDoneRef = useRef(false);

  useFocusEffect(
    useCallback(() => {
      trackAuthEvent('chat_screen_viewed', {
        session_id: guard.viewSessionId,
        sessions_count: latestSessionsCountRef.current,
        has_session: Boolean(guard.viewSessionId),
      });
      // Keep credit status fresh (picks up changes from analyses)
      refreshStatus();
      return () => {};
    }, [])
  );

  // Initial load - only runs once on mount
  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;

    const initialize = async () => {
      setLoading(true);

      // Check params SYNCHRONOUSLY before any async work
      const parsedSessionId = sessionIdParam ? Number(sessionIdParam) : undefined;
      const wantsNewSession = startNewParam === 'true';
      const wantsSessionsList = showSessionsListParam === 'true';
      const suggestedQuestion = suggestedQuestionParam?.trim();

      // Handle new session request BEFORE loading
      if (wantsNewSession) {
        guard.startNew();
        setCurrentSessionId(null);
        setMessages([]);
      }

      // Load sessions - auto-select first ONLY if not starting new and no specific session
      const shouldAutoSelect = !wantsNewSession && !parsedSessionId;
      await loadSessions({ autoSelectFirst: shouldAutoSelect });

      // If a specific session was requested, select it
      if (parsedSessionId && Number.isFinite(parsedSessionId) && !wantsNewSession) {
        showSession(parsedSessionId);
      }

      // Show sessions list if requested
      if (wantsSessionsList) {
        setShowSessionsList(true);
      }

      if (suggestedQuestion) {
        setInputText(suggestedQuestion);
      }

      await Promise.all([
        loadQuotaStatus(),
        loadAudioUploadLimits(),
        loadTranscriptionUsage(),
        loadRecentDocuments(),
      ]);
      setLoading(false);

      // Clean up params after handling (don't use router.replace during initial load)
    };

    initialize();
    // Intentionally not including params in deps - we only want this to run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load messages when session changes. "New chat" keeps whatever the send
  // flow put on screen (optimistic bubbles); loadMessages ignores sessions
  // that aren't on screen.
  useEffect(() => {
    if (currentSessionId) {
      loadMessages(currentSessionId);
    } else if (!guard.startingNew) {
      setMessages([]);
    }
  }, [currentSessionId, guard, loadMessages]);

  // Handle navigation params on subsequent navigations (not initial load)
  useFocusEffect(
    useCallback(() => {
      // Skip if this is initial load (handled by the initialization effect)
      if (!initialLoadDoneRef.current || loading) return;

      const parsedSessionId = sessionIdParam ? Number(sessionIdParam) : undefined;
      const startNew = startNewParam === 'true';
      const showList = showSessionsListParam === 'true';
      const suggestedQuestion = suggestedQuestionParam?.trim();

      // Only process if we have params to handle
      if (!startNew && !parsedSessionId && !showList && !suggestedQuestion) {
        // Just refresh quota on focus
        loadQuotaStatus();
        loadTranscriptionUsage();
        loadRecentDocuments();
        if (!audioUploadLimits) {
          loadAudioUploadLimits();
        }
        return;
      }

      console.log('🔄 Processing navigation params:', { startNew, parsedSessionId, showList });

      if (startNew) {
        startNewSession('deep_link');
      } else if (parsedSessionId && Number.isFinite(parsedSessionId)) {
        showSession(parsedSessionId);
      }

      if (showList) {
        trackAuthEvent('chat_sessions_list_opened', {
          source: 'deep_link',
          session_count: latestSessionsCountRef.current,
        });
        setShowSessionsList(true);
      }

      if (suggestedQuestion) {
        setInputText(suggestedQuestion);
      }

      // Clean up params after handling
      router.replace(ROUTES.TABS_CHAT);

      // Refresh quota on focus
      loadQuotaStatus();
      loadTranscriptionUsage();
      loadRecentDocuments();
      if (!audioUploadLimits) {
        loadAudioUploadLimits();
      }
    }, [sessionIdParam, startNewParam, showSessionsListParam, suggestedQuestionParam, router, loadQuotaStatus, loadTranscriptionUsage, loadRecentDocuments, loadAudioUploadLimits, audioUploadLimits, loading, showSession])
  );

  // Refresh messages on focus; only poll when WebSocket is unavailable (fallback mode)
  useFocusEffect(
    useCallback(() => {
      let intervalId: any = null;

      // On focus, do one refresh to ensure consistency (not mid-send, and
      // not in "New chat" mode: loadMessages ignores sessions off screen).
      if (currentSessionId && !guard.isLoading(currentSessionId) && !sendingMessageRef.current) {
        loadMessages(currentSessionId);
      }

      // Only start polling interval when WebSocket is unavailable
      // When WebSocket is active, task completion triggers a single message reload instead
      if (wsUnavailable) {
        console.log('📡 WebSocket unavailable - enabling polling fallback');

        const refreshIfNeeded = () => {
          if (!currentSessionId || !guard.isViewing(currentSessionId)) return;
          // Poll only when likely waiting for a response or there is recent activity.
          // Read send/analyze state through refs so toggling them doesn't
          // re-run this focus effect and reload the thread (CHAT-15).
          const hasThinking = messagesRef.current.some((m) => m.id === WAITING_BUBBLE_ID);
          if (hasThinking || sendingMessageRef.current || analyzingFileRef.current) {
            if (!guard.isLoading(currentSessionId)) {
              loadMessages(currentSessionId);
            }
          }
        };

        intervalId = setInterval(refreshIfNeeded, 2000);
      } else {
        console.log('🔌 WebSocket connected - using real-time events (no polling)');
      }

      return () => {
        if (intervalId) clearInterval(intervalId);
      };
    }, [currentSessionId, guard, loadMessages, wsUnavailable, analyzingFileRef, sendingMessageRef])
  );

  useEffect(() => {
    if (!isWaitingForResponse) return;
    setMessages(prev =>
      prev.map(message =>
        message.id === WAITING_BUBBLE_ID
          ? { ...message, content: t(waitingTipKey) }
          : message
      )
    );
  }, [WAITING_BUBBLE_ID, waitingTipKey, isWaitingForResponse, t, language]);

  // Streaming display: the reply's tokens go into the thread only while its
  // conversation is on screen (CHAT-4).
  useChatStreaming({
    activeTaskId,
    isStreamOnScreen: () => guard.isViewing(activeOperationSessionRef.current),
    setMessages,
  });

  const lastMessage = messages[messages.length - 1];
  const lastMessageScrollSignature = lastMessage
    ? `${lastMessage.id}:${lastMessage.content?.length ?? 0}`
    : 'empty';

  useEffect(() => {
    if (messages.length === 0 || !shouldAutoScrollRef.current) return;
    queueScrollToBottom(true);
  }, [lastMessageScrollSignature, messages.length, queueScrollToBottom, shouldAutoScrollRef]);

  // =================== ACTIONS ===================

  const sendMessage = useChatSend({
    ops,
    guard,
    inputText,
    setInputText,
    sessions,
    currentSessionId,
    language,
    t,
    waitingTipKey,
    chatLocked,
    paidChatLimitExceeded,
    quotaStatus,
    trialStatus,
    isTrialSession,
    refreshTrialStatus,
    setShowTrialLimitModal,
    setMessages,
    setSendingMessage,
    setActiveTaskId,
    chatSendIntent,
    adoptSession,
    addOptimisticMessage,
    addPendingTask,
    removePendingTask,
    loadMessages,
    loadSessions,
    loadQuotaStatus,
    waitForTaskResult,
    showTip,
  });

  const {
    showUploadPromptModal,
    setShowUploadPromptModal,
    uploadPromptMessage,
    submitToolResults,
    handleUploadPromptCancel,
    reportDocumentPickCancelled,
    reportAudioPickCancelled,
  } = useChatToolCalls({
    ops,
    guard,
    currentSessionId,
    language,
    t,
    waitingTipKey,
    router,
    setMessages,
    setSendingMessage,
    setActiveTaskId,
    loadMessages,
    loadSessions,
    loadQuotaStatus,
    loadRecentDocuments,
    trialStatus,
    refreshTrialStatus,
    waitForTaskResult,
  });

  const {
    showInstructionsModal,
    pendingFile,
    pickDocumentForAnalysis,
    cancelAnalysis,
    handleAnalyzeTranscriptWithModal,
    handleAnalysisModalSubmit,
    showAnalysisProgress,
    setShowAnalysisProgress,
    analysisTaskId,
    setAnalysisTaskId,
    analysisSessionId,
    setAnalysisSessionId,
    analyzingDocumentName,
    setAnalyzingDocumentName,
    analysisRetryAvailable,
    setAnalysisRetryAvailable,
    analysisRetryRef,
    analysisAutoOpenRef,
    analysisCompletionHandledRef,
    handleAnalysisProgressClose,
    handleAnalysisProgressCancel,
    handleViewAnalysisResult,
    handleAnalysisComplete,
    handleAnalysisError,
    handleAnalysisRetry,
  } = useDocumentAnalysis({
    ops,
    language,
    t,
    waitingTipKey,
    currentSessionId,
    canIncludeInIcfProgress,
    analyzingFile,
    setAnalyzingFile,
    activeTaskId,
    setActiveTaskId,
    setMessages,
    showSession,
    adoptSession,
    addOptimisticMessage,
    addPendingTask,
    removePendingTask,
    loadMessages,
    loadSessions,
    loadQuotaStatus,
    loadRecentDocuments,
    waitForTask,
    trialStatus,
    refreshTrialStatus,
    refreshStatus,
    checkCredits,
    showTip,
    transcriptBlocked,
    showTranscriptLimitWarning,
    reportDocumentPickCancelled,
    handleAnalysisMilestone,
    markSessionAnalyzed,
    viewingTranscript,
    setViewingTranscript,
    setShowTranscriptViewer,
  });

  const {
    showTranscriptionModal,
    showTranscriptionProgress,
    pendingAudioFile,
    transcriptionStatus,
    transcriptionEstimate,
    transcriptionTaskId,
    handleAudioFileSelected,
    handleTranscriptionCancelled,
    handleStartTranscription,
    handleTranscriptionProgressCancel,
  } = useVoiceTranscription({
    ops,
    guard,
    language,
    t,
    currentSessionId,
    canIncludeInIcfProgress,
    audioUploadLimits,
    wsUnavailable,
    transcriptBlocked,
    showTranscriptLimitWarning,
    showSession,
    adoptSession,
    loadMessages,
    loadSessions,
    loadQuotaStatus,
    loadTranscriptionUsage,
    loadRecentDocuments,
    waitForTaskResult,
    trialStatus,
    refreshTrialStatus,
    addPendingTask,
    removePendingTask,
    markSessionAnalyzed,
    setAnalyzingFile,
    setViewingTranscript,
    submitToolResults,
    reportAudioPickCancelled,
    analysisProgress: {
      setShowAnalysisProgress,
      setAnalysisTaskId,
      setAnalysisSessionId,
      setAnalyzingDocumentName,
      setAnalysisRetryAvailable,
      analysisRetryRef,
      analysisAutoOpenRef,
      analysisCompletionHandledRef,
    },
  });

  // ─── Reference-file flow (chat-only context, no analysis) ─────────────
  /**
   * Session for an attach. In "New chat" mode this creates the session and
   * puts it on screen, which also ends "New chat" mode, so the next message
   * goes to the same session and the reference file is in its context
   * (CHAT-3).
   */
  const ensureSessionForAttach = useCallback(async (): Promise<number> => {
    if (!guard.startingNew && guard.viewSessionId != null) {
      return guard.viewSessionId;
    }
    const created = await chatAPI.createSession();
    adoptSession(created.id);
    await loadSessions();
    return created.id;
  }, [adoptSession, guard, loadSessions]);

  const {
    referenceFiles,
    attachingReference,
    attachReferenceFile,
    attachReferenceFromFile,
    removeReferenceFile,
  } = useReferenceFiles({ guard, currentSessionId, ensureSession: ensureSessionForAttach, t });

  // Wire native HTML drag-and-drop on the web composer wrapper. Dropped files
  // are attached as reference files (the "no-analysis" path) — analysis
  // uploads still go through the existing pickDocumentForAnalysis flow,
  // which has its own confirmation modal. `loading` is a dependency because
  // the composer only mounts once the first load finishes (CHAT-23).
  React.useEffect(() => {
    if (Platform.OS !== ('web' as any)) return;
    const node: HTMLElement | null = composerWrapRef.current as any;
    if (!node || typeof node.addEventListener !== 'function') return;

    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      setDragOver(true);
    };
    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Only clear when we actually leave the wrapper (not a child).
      if (e.target === node) setDragOver(false);
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;
      // Attach first file only; users can repeat for additional files.
      attachReferenceFromFile(files[0]);
    };

    node.addEventListener('dragover', onDragOver);
    node.addEventListener('dragleave', onDragLeave);
    node.addEventListener('drop', onDrop);
    return () => {
      node.removeEventListener('dragover', onDragOver);
      node.removeEventListener('dragleave', onDragLeave);
      node.removeEventListener('drop', onDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId, attachingReference, loading]);


  const handleUploadPromptConfirm = useCallback(() => {
    setShowUploadPromptModal(false);
    pickDocumentForAnalysis();
  }, [pickDocumentForAnalysis, setShowUploadPromptModal]);

  const {
    showRenameModal,
    newSessionTitle,
    setNewSessionTitle,
    startNewSession,
    switchToSession,
    openRecentDocument,
    openRenameModal,
    confirmDeleteSession,
    handleRenameSession,
    cancelRename,
  } = useSessionActions({
    ops,
    guard,
    t,
    router,
    showToast,
    currentSessionId,
    setCurrentSessionId,
    setSessions,
    setMessages,
    setInputText,
    setShowSessionsList,
    showSession,
    clearSession,
    setViewingTranscript,
    setShowTranscriptViewer,
  });

  const {
    shareTranscriptId,
    shareTranscriptName,
    shareTranscriptModalVisible,
    setShareTranscriptModalVisible,
    handleExportAnalysisPdf,
    getCurrentSourceDocumentId,
    openCurrentTranscript,
    openShareCurrentTranscript,
  } = useSessionExports({
    language,
    t,
    sessions,
    currentSessionId,
    getSessionSourceFilename,
    viewingTranscript,
    setViewingTranscript,
    setShowTranscriptViewer,
  });

  // =================== RENDER FUNCTIONS ===================

  // "Download PDF" sits on the analysis output only: the first visible
  // assistant message of an analysis session.
  const analysisOutputMessageId = React.useMemo(() => {
    const sessionType = (currentSession?.session_type || '').toLowerCase();
    if (!currentSessionId || !['analysis', 'analyze', 'hybrid'].includes(sessionType)) return null;
    const first = messages.find((m) => m.role === 'assistant' && !(m as any).is_context);
    return first ? first.id : null;
  }, [currentSession?.session_type, currentSessionId, messages]);

  // Stable callback for the memoized rows; always exports the session on screen.
  const exportPdfRef = useRef<(sessionId: number) => void>(() => {});
  exportPdfRef.current = (sessionId: number) => { void handleExportAnalysisPdf(sessionId); };
  const exportCurrentSessionPdf = useCallback(() => {
    if (guard.viewSessionId != null) exportPdfRef.current(guard.viewSessionId);
  }, [guard]);

  const waitingTip = t(waitingTipKey);
  const renderMessage = useCallback(({ item }: { item: ChatMessage }) => (
    <ChatMessageRow
      item={item}
      isPhone={isPhone}
      waitingTip={waitingTip}
      showPdfButton={item.id === analysisOutputMessageId}
      onExportPdf={exportCurrentSessionPdf}
      reportSessionId={item.id === analysisOutputMessageId ? currentSessionId : null}
    />
  ), [analysisOutputMessageId, currentSessionId, exportCurrentSessionPdf, isPhone, waitingTip]);

  const renderAnalysisInstructionsModal = () => (
    <AnalysisModal
      visible={showInstructionsModal}
      onClose={cancelAnalysis}
      onSubmit={handleAnalysisModalSubmit}
      transcriptName={pendingFile?.name}
      loading={analyzingFile}
    />
  );

  // =================== MAIN RENDER ===================

  const renderChatShellLoading = () => (
    <WebAppShell active="chat" contentMaxWidth="100%">
      <View style={webChatStyles.stateCard}>
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={webChatStyles.stateTitle}>{t('chat.loading')}</Text>
      </View>
    </WebAppShell>
  );

  if (loading) {
    return renderChatShellLoading();
  }

  const dismissActivationCelebration = () => {
    setShowActivationCelebration(false);
    showTip('tip_pdf_export');
    showTip('tip_first_analysis_done');
  };

  // Layout (CHAT-1): the shell hands us a bounded flex:1/minHeight:0 box at
  // every width. The chat column fills it, the header / usage strip /
  // composer keep their height, and the thread is the only scroller, so the
  // composer is always on screen and scrollToEnd targets the real scroller.
  // Below the desktop breakpoint the file-summary panel scrolls inside the
  // thread instead of stacking under the column.
  const currentTitle = currentSessionId
    ? (currentSession?.title || `${t('chat.session')} ${currentSessionId}`)
    : t('navigation.chat');
  const currentSourceDocumentId = getCurrentSourceDocumentId();

  const recentItems = buildChatRecentItems({
    t,
    recentDocuments,
    sessions,
    currentSessionId,
    getSessionDisplayTitle,
    onOpenDocument: openRecentDocument,
    onOpenSession: switchToSession,
  });
  const suggestedPrompts = chatSuggestedPrompts(t, {
    reassessOnly,
    allowMarkerTable: tierAccess.allowMarkerTable,
  });
  const usageCards = buildChatUsageCards({
    t,
    trialUsage: activeTrialUsage,
    quotaStatus,
    quotaLoadFailed,
    creditStatus,
    transcriptionUsage,
  });
  const contextPanel = currentSessionId && sessionAnalyzed ? (
    <ChatContextPanel
      title={currentTitle}
      messageCount={messages.length}
      isPhone={isPhone}
      isCompact={isCompact}
      canOpenTranscript={Boolean(currentSourceDocumentId)}
      canExportObservationPdf={tierAccess.allowCustomMode}
      canShareTranscript={Boolean(isB2BUser && currentSourceDocumentId)}
      onOpenTranscript={openCurrentTranscript}
      onExportPdf={(reportType) => handleExportAnalysisPdf(currentSessionId, reportType)}
      onShareTranscript={openShareCurrentTranscript}
    />
  ) : null;
  const inlineContextPanel = isCompact ? contextPanel : null;
  const composerBusy = sendingMessage || analyzingFile;

  return (
    <WebAppShell active="chat" recentItems={recentItems} contentMaxWidth="100%" scroll={false}>
      <View style={webChatStyles.root} testID="chat_root">
        <View style={webChatStyles.mainColumn}>
          <ChatHeader
            title={currentTitle}
            isPhone={isPhone}
            isCompact={isCompact}
            // "Transcript ready" = the session has a linked source transcript (CHAT-7).
            showTranscriptReady={hasLinkedTranscript(currentSession)}
            showAnalysisDone={sessionAnalyzed}
            creditLabel={creditChipLabel(t, creditStatus)}
            onOpenHistory={() => setShowSessionsList(true)}
            onNewChat={() => startNewSession('header_button')}
            onAudioFileSelected={handleAudioFileSelected}
            audioLoading={showTranscriptionProgress}
            audioDisabled={composerBusy || transcriptBlocked}
          />

          <ChatUsageStrip cards={usageCards} isPhone={isPhone} isCompact={isCompact} />

          <LowCreditWarningBanner
            creditStatus={creditStatus}
            onBuyCredits={() => setShowCreditPurchaseModal(true)}
          />

          {trialStatus?.active && currentSessionId !== null && isTrialSession(currentSessionId) && (
            <TrialBanner
              expiresAt={trialStatus.expires_at}
              messagesRemaining={trialStatus.messages_remaining}
              chatCount={trialStatus.chat_count}
              kind={trialStatus.kind}
              grandfathered={trialStatus.grandfathered}
            />
          )}

          <ChatThread
            messages={messages}
            isPhone={isPhone}
            isCompact={isCompact}
            inlineContextPanel={inlineContextPanel}
            flatListRef={flatListRef}
            renderMessage={renderMessage}
            shouldAutoScrollRef={shouldAutoScrollRef}
            pendingScrollToBottomRef={pendingScrollToBottomRef}
            queueScrollToBottom={queueScrollToBottom}
            onOpenSample={() => setShowSampleReport(true)}
            onUploadDocument={pickDocumentForAnalysis}
            onUploadAudio={() => pickAudioForTranscriptionRef.current()}
          />

          {trialStatus?.expired && !canViewOwnReports && currentSessionId !== null && isTrialSession(currentSessionId) && (
            <TrialResultsLock />
          )}

          <ChatComposer
            isPhone={isPhone}
            composerRef={composerWrapRef}
            inputText={inputText}
            onChangeText={setInputText}
            onSend={sendMessage}
            onFocus={() => {
              setShowAttachMenu(false);
              queueScrollToBottom(false);
            }}
            placeholder={composerPlaceholder(t, {
              dragOver,
              chatLocked,
              reassessOnly,
              isTrialExhausted: Boolean(isTrialExhausted),
              analyzingFile,
              paidChatLimitExceeded,
            })}
            editable={!composerBusy && !paidChatLimitExceeded && !isTrialExhausted && !chatLocked}
            canSend={!composerBusy && !paidChatLimitExceeded && !isTrialExhausted && !chatLocked}
            busy={composerBusy}
            attachBusy={analyzingFile || attachingReference}
            dragOver={dragOver}
            dailyLimit={paidChatLimitExceeded && quotaStatus
              ? { used: quotaStatus.usage.daily_chats_used, limit: quotaStatus.usage.daily_chats_limit }
              : null}
            chatLock={chatLocked || reassessOnly
              ? { onSeePlans: () => router.push(ROUTES.GATED_PLAN_SELECTION as any), reassessOnly }
              : null}
            suggestedPrompts={suggestedPrompts}
            showSuggestions={messages.length > 0}
            referenceFiles={referenceFiles}
            onRemoveReference={removeReferenceFile}
            showAttachMenu={showAttachMenu}
            onToggleAttachMenu={() => setShowAttachMenu((v) => !v)}
            onPickAnalysisFile={() => {
              setShowAttachMenu(false);
              pickDocumentForAnalysis();
            }}
            onAttachReference={() => {
              setShowAttachMenu(false);
              attachReferenceFile();
            }}
          />
        </View>

        {!isCompact && contextPanel}
      </View>

      {renderAnalysisInstructionsModal()}
      <SessionsSheet
        visible={showSessionsList}
        onClose={() => setShowSessionsList(false)}
        sessions={sessions}
        currentSessionId={currentSessionId}
        onNewChat={() => startNewSession()}
        onSelect={switchToSession}
        onRename={openRenameModal}
        onDelete={confirmDeleteSession}
      />
      <RenameSessionDialog
        visible={showRenameModal}
        title={newSessionTitle}
        onChangeTitle={setNewSessionTitle}
        onCancel={cancelRename}
        onSave={handleRenameSession}
      />
      <UploadPromptDialog
        visible={showUploadPromptModal}
        message={uploadPromptMessage}
        onCancel={handleUploadPromptCancel}
        onConfirm={handleUploadPromptConfirm}
      />
      <ZeroCreditBlocker
        visible={showBlocker}
        creditStatus={creditStatus}
        onClose={hideBlocker}
        onBuyCredits={() => {
          hideBlocker();
          setShowCreditPurchaseModal(true);
        }}
        onUpgradePlan={() => {
          hideBlocker();
          router.push(ROUTES.PROFILE);
        }}
      />
      <CreditPurchaseModal
        visible={showCreditPurchaseModal}
        onClose={() => setShowCreditPurchaseModal(false)}
        onPurchaseComplete={() => {
          setShowCreditPurchaseModal(false);
          refreshStatus();
        }}
      />
      <TranscriptionModal
        visible={showTranscriptionModal}
        onClose={handleTranscriptionCancelled}
        onStart={handleStartTranscription}
        file={pendingAudioFile}
        loading={showTranscriptionProgress}
      />
      <TranscriptionProgress
        visible={showTranscriptionProgress}
        status={transcriptionStatus}
        estimatedMinutes={2}
        estimatedSeconds={transcriptionEstimate?.seconds}
        estimatedMinSeconds={transcriptionEstimate?.minSeconds}
        estimatedMaxSeconds={transcriptionEstimate?.maxSeconds}
        taskId={transcriptionTaskId}
        onCancel={handleTranscriptionProgressCancel}
      />
      <AnalysisProgress
        visible={showAnalysisProgress}
        onClose={handleAnalysisProgressClose}
        taskId={analysisTaskId}
        sessionId={analysisSessionId}
        documentName={analyzingDocumentName || undefined}
        onViewAnalysis={handleViewAnalysisResult}
        onExportPdf={handleExportAnalysisPdf}
        onComplete={handleAnalysisComplete}
        onError={handleAnalysisError}
        onRetry={analysisRetryAvailable ? () => { void handleAnalysisRetry(); } : undefined}
        onCancel={handleAnalysisProgressCancel}
      />
      <TranscriptViewer
        visible={showTranscriptViewer}
        onClose={() => {
          setShowTranscriptViewer(false);
          setViewingTranscript(null);
        }}
        transcript={viewingTranscript}
        onReturnToSession={() => {
          setShowTranscriptViewer(false);
          setViewingTranscript(null);
        }}
        onRefreshFeedback={handleAnalyzeTranscriptWithModal}
      />
      {shareTranscriptId && (
        <ShareDocumentModal
          visible={shareTranscriptModalVisible}
          onClose={() => setShareTranscriptModalVisible(false)}
          documentId={shareTranscriptId}
          documentName={shareTranscriptName}
          onShareSuccess={() => setShareTranscriptModalVisible(false)}
        />
      )}
      <TrialChatLimitModal
        visible={showTrialLimitModal}
        onDismiss={() => setShowTrialLimitModal(false)}
      />
      <TrialExpiredModal
        visible={showTrialExpiredModal}
        onDismiss={() => setShowTrialExpiredModal(false)}
      />
      <ActivationCelebration
        visible={showActivationCelebration}
        isPhone={isPhone}
        onDismiss={dismissActivationCelebration}
      />
      <SampleReportViewer
        visible={showSampleReport}
        surface="chat_empty_state"
        primaryAction="upload"
        onClose={() => setShowSampleReport(false)}
        onPrimaryPress={() => {
          setShowSampleReport(false);
          pickDocumentForAnalysis();
        }}
      />
    </WebAppShell>
  );
}
